import { Injectable } from "@angular/core";
import { UiUser, UserRole } from "../models/ui-user.model";
import { Observable } from "rxjs";
import { ServiceOptions } from "../models/service-options";
import { HttpClient } from "@angular/common/http";
import { AuthService } from "./auth.service";
import { map, catchError, take } from "rxjs/operators";
import { DataElement, Doctor, ServiceCallInfo } from "../models/common";
import { ServiceMetaData } from "../models/service-meta.model";
import {
  OrderSearchParams,
  Order,
  EditableParams,
  SystemParam,
  SystemParamSubject,
  RXOpt,
  RXOptValue,
  RXNote,
} from "../models/order.model";
import { Service } from "aws-sdk";
import { DatePipe } from "@angular/common";
import { Clinic } from "../models/clinic.model";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import {
  GET_AGENTS_LIST,
  GET_DELIVERERS_LIST,
  GET_AGENT_CLINIC_LIST,
  GET_CHAT_TASK_STATUS_LIST,
  GET_CLINIC_CHAIN_LIST,
  GET_CLINIC_LIST,
  GET_DOCTOR_LIST,
  GET_ORDER_EMAIL_TEMPLATE_LIST,
  GET_PLANT_LIST,
  GET_ROLE_LIST,
  GET_RX_OPT,
  GET_RX_PRESET_NOTES,
  GET_STAGE_LIST,
  GET_SUPPLY_TYPES_LIST,
  GET_SYSTEM_PARAMS,
  GET_TECHINICIANS_LIST,
  GET_TECH_ISSUES_LIST,
  GET_URGENCY_LIST,
} from "../ngrx/ngrx-actions";

@Injectable({
  providedIn: "root",
})
export class DataService {
  mockupSw: boolean = true;
  private mockupDataUrl = "assets/mock/";

  constructor(
    private readonly store: Store,
    private http: HttpClient,
    private authService: AuthService,
    private translateService: TranslateService
  ) { }

  getClinicList(
    agentList: boolean = false,
    agentCode: number = null
  ): Observable<Clinic[]> {
    let response = new Observable<Clinic[]>();
    let serviceName = "get_clinic_list";
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    if (agentCode) params["agentCode"] = agentCode;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractClinicList(data, info, agentList);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractClinicList(
    res,
    info: ServiceCallInfo,
    agentList: boolean
  ): Clinic[] {
    this.authService.extractRequestStatusNew(info, res);
    let list: Clinic[] = [];
    let data = res.reply.requestResult.data;
    if (data && data.row) {
      let arr = new Array().concat(data.row);
      arr.forEach((i) => {
        let plantUserSw = this.authService.currentUser.org == "PLANT";
        let el = new Clinic(
          i.clinicId,
          plantUserSw ? i.clinicId : i.clinicDescr
        );
        el.deliveryEmail = i.deliveryEmail;
        el.deliveryApiSw = this.authService.fromXMLBoolean(i.deliveryApiSw);
        list.push(el);
      });
    }

    if (agentList) {
      this.store.dispatch(
        GET_AGENT_CLINIC_LIST({
          data: list,
        })
      );
    } else {
      this.store.dispatch(
        GET_CLINIC_LIST({
          data: list,
        })
      );
    }

    return list;
  }

  getDoctorList(): Observable<Doctor[]> {
    let response = new Observable<Doctor[]>();
    let serviceName = "get_doctors";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractDoctorList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractDoctorList(res, info: ServiceCallInfo): Doctor[] {
    this.authService.extractRequestStatusNew(info, res);
    let list: Doctor[] = [];
    let data = res.reply.requestResult.data;
    if (data && data.row) {
      let arr = new Array().concat(data.row);
      arr.forEach((i) => {
        let el = new Doctor();
        let plantUserSw = this.authService.currentUser.org == "PLANT";
        el.id = i.doctorId;
        el.text = plantUserSw ? el.id : i.fullName;
        if (i.clinicIdList) {
          if ((i.clinicIdList as string).toString().includes(",")) {
            el.clinicList = (i.clinicIdList as string).toString().split(",");
          } else {
            el.clinicList = [i.clinicIdList.toString()];
          }
        }
        list.push(el);
      });
    }

    this.store.dispatch(
      GET_DOCTOR_LIST({
        data: list,
      })
    );

    return list;
  }

  getStageList(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_stages";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractStageList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractStageList(res, info: ServiceCallInfo): DataElement[] {
    this.authService.extractRequestStatusNew(info, res);
    let list: DataElement[] = [];
    let data = res.reply.requestResult.data;
    if (data && data.stages && data.stages.stage) {
      let arr = new Array().concat(data.stages.stage);
      arr.forEach((i) => {
        let el = new DataElement(i.stgId, i.stgStage);
        el.altFilter = i.stgCode;
        list.push(el);
      });
    }

    this.store.dispatch(
      GET_STAGE_LIST({
        data: list,
      })
    );

    return list;
  }

  getSuppliesTypes(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_supplies_types";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractSuppliesTypes(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractSuppliesTypes(res, info: ServiceCallInfo): DataElement[] {
    const results: DataElement[] = this.mapToDataElement(
      res,
      info,
      "itemType",
      "itemDescr"
    );

    this.store.dispatch(
      GET_SUPPLY_TYPES_LIST({
        data: results,
      })
    );

    return results;
  }

  private mapToDataElement(
    res,
    info: ServiceCallInfo,
    idProp: string,
    textProp: string,
    rowProp: string = "row"
  ): DataElement[] {
    this.authService.extractRequestStatusNew(info, res);

    if (!res || !res.reply.requestResult.data) return [];

    let dataList = new Array<DataElement>();
    let data = res.reply.requestResult.data[rowProp];

    if (!data) {
      return [];
    }

    if (data.length) {
      dataList = data.map((item: any) => {
        return new DataElement(item[idProp], item[textProp]);
      });
    } else {
      dataList = [new DataElement(data[idProp], data[textProp])];
    }

    return dataList;
  }

  getPlantList(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_plant_list";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractPlantList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractPlantList(res, info: ServiceCallInfo): DataElement[] {
    const list: DataElement[] = this.mapToDataElement(
      res,
      info,
      "plantId",
      "plantDescr"
    );

    this.store.dispatch(
      GET_PLANT_LIST({
        data: list,
      })
    );

    return list;
  }

  getRoleList(): Observable<UserRole[]> {
    let response = new Observable<UserRole[]>();
    let serviceName = "get_user_roles";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractRoleList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractRoleList(res, info: ServiceCallInfo): UserRole[] {
    this.authService.extractRequestStatusNew(info, res);

    const list: UserRole[] = [];
    let data = res.reply.requestResult.data;
    if (data && data.row) {
      let arr = new Array().concat(data.row);
      arr.forEach((i) => {
        let el = new UserRole(i.roleCode, i.roleDescr, i.org);
        list.push(el);
      });
    }

    this.store.dispatch(
      GET_ROLE_LIST({
        data: list,
      })
    );

    return list;
  }

  getUrgencyOptions(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_urgency_options";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractUrgencyOptions(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractUrgencyOptions(res, info: ServiceCallInfo): DataElement[] {
    const results: DataElement[] = this.mapToDataElement(
      res,
      info,
      "code",
      "descr"
    );

    this.store.dispatch(
      GET_URGENCY_LIST({
        data: results,
      })
    );

    return results;
  }

  getSystemParams(): Observable<SystemParamSubject[]> {
    let response = new Observable<SystemParamSubject[]>();
    let serviceName = "get_system_params";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractSystemParams(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractSystemParams(
    res,
    info: ServiceCallInfo
  ): SystemParamSubject[] {
    this.authService.extractRequestStatusNew(info, res);
    let list: SystemParamSubject[] = [];
    let data = res.reply.requestResult.data;
    if (data && data.row) {
      let arr = new Array().concat(data.row);
      arr.forEach((i) => {
        let subj = list.find((s) => s.subjectCode == i.subject);
        if (!subj) {
          subj = new SystemParamSubject();
          subj.subjectCode = i.subject;
          subj.subjectDescr = this.translateService.instant(
            "translate.params.subj_" + subj.subjectCode
          );
          if (subj.subjectCode == "DUE_DATE_ADJUST") {
            subj.min = -100;
          }
          list.push(subj);
        }

        let p = new SystemParam();
        p.code = i.code;
        p.value = i.valueTxt;
        p.descr = this.translateService.instant(
          "translate.params.subj_" + subj.subjectCode + "__" + p.code
        );
        subj.params.push(p);
      });
    }

    this.store.dispatch(
      GET_SYSTEM_PARAMS({
        data: list,
      })
    );

    return list;
  }

  getTaskStatusList(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_task_status_list";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractTaskStatusList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractTaskStatusList(res, info: ServiceCallInfo): DataElement[] {
    const results: DataElement[] = this.mapToDataElement(
      res,
      info,
      "code",
      "descr"
    );

    this.store.dispatch(
      GET_CHAT_TASK_STATUS_LIST({
        data: results,
      })
    );

    return results;
  }

  getAgentsList(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_agents_list";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractAgentsList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractAgentsList(res, info: ServiceCallInfo): DataElement[] {
    const results: DataElement[] = this.mapToDataElement(
      res,
      info,
      "code",
      "descr",
      "agents"
    );

    this.store.dispatch(
      GET_AGENTS_LIST({
        data: results,
      })
    );

    return results;
  }

  getClinicChainList(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_clinic_chain_list";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractClinicChainList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractClinicChainList(res, info: ServiceCallInfo): DataElement[] {
    this.authService.extractRequestStatusNew(info, res);
    const results: DataElement[] = this.mapToDataElement(
      res,
      info,
      "code",
      "descr",
      "chains"
    );

    this.store.dispatch(
      GET_CLINIC_CHAIN_LIST({
        data: results,
      })
    );

    return results;
  }

  getRXOpt(): Observable<RXOpt[]> {
    let response = new Observable<RXOpt[]>();
    let serviceName = "get_rx_opt";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (false)
      return this.http.get(this.mockupDataUrl + "get_rx_opt.json").pipe(
        map((data) => {
          return this.extractRXOpt(data, info);
        })
      );

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractRXOpt(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractRXOpt(res, info: ServiceCallInfo): RXOpt[] {
    this.authService.extractRequestStatusNew(info, res);

    const list: RXOpt[] = [];
    let data = res.reply.requestResult.data;
    if (data && data.rows) {
      let arr = new Array().concat(data.rows);
      arr.forEach((i) => {
        let opt = new RXOpt(i.optCode, i.optDescr);
        opt.lineType = i.lineType;
        opt.valueType = i.valueType;
        opt.displayType = i.displayType;
        if (opt.valueType == "LIST" && !opt.displayType) {
          opt.displayType = "DROPDOWN";
        }
        if (opt.valueType == "BOOL" && !opt.displayType) {
          opt.displayType = "CHECKBOX";
        }
        opt.icon = i.iconKey;
        opt.drPrefSw = i.drPrefSw;
        opt.max = i.max;
        opt.min = i.min;
        opt.drPrefDescr = i.drPrefDescr;

        if (i.valueList) {
          let arrV = new Array().concat(i.valueList);
          arrV.forEach((v) => {
            let val = new RXOptValue(v.valueCode, v.valueDescr);
            val.iconKey = v.iconKey;
            opt.valueList.push(val);
          });
        }

        list.push(opt);
      });
    }

    this.store.dispatch(
      GET_RX_OPT({
        data: list,
      })
    );

    return list;
  }

  getTechIssuesList(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_tech_issues_list";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (false)
      return this.http
        .get(this.mockupDataUrl + "get_tech_issues_list.json")
        .pipe(
          map((data) => {
            return this.extractTechIssuesList(data, info);
          })
        );

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractTechIssuesList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractTechIssuesList(res, info: ServiceCallInfo): DataElement[] {
    this.authService.extractRequestStatusNew(info, res);

    const list: DataElement[] = [];
    let data = res.reply.requestResult.data;
    if (data && data.rows) {
      let arr = new Array().concat(data.rows);
      arr.forEach((i) => {
        let el = new DataElement(i.code, i.descr);
        el.icon = i.iconKey;
        list.push(el);
      });
    }

    this.store.dispatch(
      GET_TECH_ISSUES_LIST({
        data: list,
      })
    );

    return list;
  }

  getTechniciansList(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_technicians_list";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (false)
      return this.http
        .get(this.mockupDataUrl + "get_technicians_list.json")
        .pipe(
          map((data) => {
            return this.extractTechniciansList(data, info);
          })
        );

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractTechniciansList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractTechniciansList(res, info: ServiceCallInfo): DataElement[] {
    const results: DataElement[] = this.mapToDataElement(
      res,
      info,
      "technicianId",
      "name",
      "rows"
    );

    this.store.dispatch(
      GET_TECHINICIANS_LIST({
        data: results,
      })
    );

    return results;
  }


  getDeliverersList(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_deliverers";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractDeliverersList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractDeliverersList(res, info: ServiceCallInfo): DataElement[] {
    this.authService.extractRequestStatusNew(info, res);
    let data = res.reply.requestResult.data;
    const results: DataElement[] = data["deliverers"].map(i => new DataElement(i, i));

    this.store.dispatch(
      GET_DELIVERERS_LIST({
        data: results,
      })
    );

    return results;
  }

  getRXPresetNotes(formId?: number, profileId?: number): Observable<RXNote[]> {
    let response = new Observable<RXNote[]>();
    let serviceName = "get_rx_preset_notes";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (formId) {
      params["formId"] = formId;
    }
    if (profileId) {
      params["profileId"] = profileId;
    }

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (false)
      return this.http
        .get(this.mockupDataUrl + "get_rx_preset_notes.json")
        .pipe(
          map((data) => {
            return this.extractRXPresetNotes(data, info);
          })
        );

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractRXPresetNotes(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractRXPresetNotes(res, info: ServiceCallInfo): RXNote[] {
    this.authService.extractRequestStatusNew(info, res);

    const list: RXNote[] = [];
    let data = res.reply.requestResult.data;
    if (data && data.notes) {
      let arr = new Array().concat(data.notes);
      arr.forEach((i) => {
        let note = new RXNote();
        note.presetNoteId = i.noteId;
        note.text = i.text;
        list.push(note);
      });
    }

    this.store.dispatch(
      GET_RX_PRESET_NOTES({
        data: list,
      })
    );

    return list;
  }

  getOrderEmailTemplateList(): Observable<DataElement[]> {
    let response = new Observable<DataElement[]>();
    let serviceName = "get_order_email_template_list";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractOrderEmailTemplateList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractOrderEmailTemplateList(
    res,
    info: ServiceCallInfo
  ): DataElement[] {
    this.authService.extractRequestStatusNew(info, res);

    const list: DataElement[] = [];
    let data = res.reply.requestResult.data;
    if (data) {
      let arr = new Array().concat(data);
      arr.forEach((i) => {
        list.push(new DataElement(i.temlateCode, i.descr));
      });
    }

    this.store.dispatch(
      GET_ORDER_EMAIL_TEMPLATE_LIST({
        data: list,
      })
    );

    return list;
  }
}
